import React, { createContext, useContext, useEffect, useState } from 'react';
import ModalTask from 'components/Organisms/Modals/ModalTask/ModalTask';
import iconCortPng from 'assets/images/home.png';
import Step1 from './Step1/Step1';
import { Button } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import Step2 from './Step2/Step2';
import {
  Entity,
  Loan,
  Task,
  TaskResponse,
  TaskState
} from '../../../../../gql/graphql';
import { useGetTaskResponsesByTask } from '../../../../../services/task/querys';
import { LoanContext } from '../../Dashboard';
import DropdownBlue from 'components/Atoms/Dropdown/DropdownBlue/DropdownBlue';
import { useCreateResponseTask } from 'services/task/mutations';
import StateTag from 'components/Atoms/Tags/StateTag/StateTag';
import { findTaskRules } from '../../RulesTaskCards.constanst';
import { ModuleKeys } from 'services/task/modules-keys.constanst';
import { TaskOpenModalContext } from '../../../Private';
import CircleTag from 'components/Atoms/Tags/CircleTag/CircleTag';
import { useModalContext } from '../../../../../sentry/contexts/useModalContext';

export const TaskContext = createContext<Task | {}>({});
export const EntityContext = createContext<Entity | {}>({});

interface Props {
  task: Task;
}

const Payoff = ({ task }: Props) => {
  const { value: openContext, setHandler }: any = useContext(TaskOpenModalContext);
  const loan: Loan | any = useContext(LoanContext);

  const { execute: createResponseTask } = useCreateResponseTask();
  const { data: taskResponses, reexecute: getTaskResponses } = useGetTaskResponsesByTask({
    variables: { findResponsesByTask: { taskId: task._id } }
  });

  const [isModalTaskVisible, setModalTaskVisible] = useState(false);
  const [isReview, setIsReview] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [step, setStep] = useState(1);
  const [responseStep1, setResponseStep1] = useState<TaskResponse | any>();
  const [responseStep2, setResponseStep2] = useState<TaskResponse>();
  const [entity, setEntity] = useState<Entity | any>();

  useEffect(() => {
    if (openContext === 'payoff' || openContext === ModuleKeys.PAYOFF) {
      setModalTaskVisible(true);
      setHandler();
    } else if (!!openContext)
      setModalTaskVisible(false);
  }, [openContext]);

  useModalContext({ isModalVisible: isModalTaskVisible, modalKey: 'payoff', task, step });

  useEffect(() => {
    if (isModalTaskVisible && taskResponses) {
      const step2 = taskResponses.find((item) => item.step === 2);
      if (step2) {
        setStep(2);
      }
    }
  }, [isModalTaskVisible, taskResponses?.length]);

  useEffect(() => {
    if (isModalTaskVisible)
      getTaskResponses({ requestPolicy: 'network-only' });
  }, [isModalTaskVisible, step])

  useEffect(() => {
    const step1: any = taskResponses?.find((item) => item.step === 1);
    setResponseStep1(step1);

    const step2: any = taskResponses?.find((item) => item.step === 2);
    setResponseStep2(step2);
    setIsReview(step2?.state === TaskState.Review);
    setIsApproved(step2?.state === TaskState.Approved);
  }, [taskResponses]);

  const onHelpDocs = () => {
    console.log('onHelpDocs');
  };

  const onChangeStep1 = async (optionKey: "letter" | "contact") => {
    const { createdAt, __typename, task, ...payload } = responseStep1;

    await createResponseTask({
      ...payload,
      metadata: {},
      task: task._id,
      key: optionKey === 'letter' ? 'yes' : 'no',
      label: optionKey === 'letter' ?
        'Yes, I have a payoff letter' : 'No, I have a payoff contact',
    });

    getTaskResponses({ requestPolicy: 'network-only' });
  };

  const circlesOtherDocs: any = () => {
    const addAnotherDocs = task.responses?.filter(item => item.key === 'doc-another');

    const docs = addAnotherDocs?.map(item =>
      ({ state: item.state, name: item.label })
    ) || [];
    return docs;
  };

  const canNavigateToStep = (targetStep: number) => {
    if (targetStep === 1) return true;
    if (targetStep === 2) {
      return responseStep1?.key === 'yes' || responseStep1?.key === 'no';
    }
    return false;
  }

  const renderTracker = () => {
    const lastFile = responseStep2?.files?.slice(-1)[0];

    const getFilesCircle = () => {
      const filesWithoutError = responseStep2?.files?.filter((item: any) => item.state !== 'request_error') || [];

      if (filesWithoutError.length > 0) {
        // Si hay archivos sin error, mostrar solo el último sin error
        return [filesWithoutError[filesWithoutError.length - 1]];
      }

      // Si todos los archivos tienen error, mostrar el último archivo
      const lastFile = responseStep2?.files?.slice(-1)[0];
      return lastFile ? [lastFile] : [];
    };

    const getTotalApproved = () => {
      const isFileSubmitted = responseStep2?.files?.filter(item => item.state !== 'request_error') || [];
      const otherDocIsSubmitted = circlesOtherDocs()?.filter((item: any) => item.state !== TaskState.RequestError) || [];
      return isFileSubmitted.length > 0 && otherDocIsSubmitted.length === 0 ? 1
        : isFileSubmitted.length > 0 && otherDocIsSubmitted.length > 0 ? 1 + otherDocIsSubmitted.length :
          isFileSubmitted.length === 0 && otherDocIsSubmitted.length > 0 ? otherDocIsSubmitted.length : 0;
    };

    return (
      <div className='payoff-footer' style={{ height: '100%' }}>
        <span className='title-summary'>Task summary</span>
        <div className="mt-20">
          <p className="placeholder">
            I have a payoff{' '}
            {step === 1 && <span >letter/contact</span>}
            {step === 2 && responseStep2 &&
              <b
                className={`bold ${responseStep2.state === TaskState.Review ? 'is-review' : ''}`}
                style={{ padding: 0 }}
              >
                {responseStep1?.key === 'yes' ? 'letter' : 'contact'}
              </b>
            }
            {step === 2 && !responseStep2 &&
              <DropdownBlue
                label={responseStep1?.key === 'yes' ? 'letter' : 'contact'}
                onClickSelect={onChangeStep1}
                items={responseStep1?.key !== 'yes' ?
                  [{ key: 'letter', label: 'Letter is not submitted' }] :
                  [{ key: 'contact', label: 'Contact is not submitted' }]
                }
                className={`dropdown-blue-bold`}
              />
            }
          </p>

          {(responseStep1?.key === 'yes' || circlesOtherDocs().length > 0) &&
            <div>
              <p
                className="placeholder"
                style={{ marginTop: 40, fontWeight: 500 }}
              >
                Document Status{' '}
              </p>

              {taskResponses && (
                <div className="owners-tags mt-5">
                  <span>{getTotalApproved()}/{
                    responseStep1?.key === 'yes'
                      ? (circlesOtherDocs()?.length > 0 ? 1 + circlesOtherDocs()?.length : 1)
                      : circlesOtherDocs()?.length
                  } documents submitted</span>
                </div>
              )}

              <div className="mt-10 circles-tags">
                {responseStep1?.key === 'yes' && getFilesCircle().map((_response, index) => {
                  let _state = _response?.state?.toUpperCase() || 'DRAFT';

                  return (
                    <CircleTag
                      key={index}
                      state={_state}
                      tagKey={_state}
                      isIconState={true}
                    />
                  );
                })}
                {circlesOtherDocs().map((item: any, index: number) => {
                  return (
                    <CircleTag
                      key={index}
                      state={item.state}
                      tagKey={item.state}
                      isIconState={true}
                    />
                  )
                })}
              </div>
            </div>
          }
        </div>
      </div>
    );
  }

  const { taskName } = findTaskRules(ModuleKeys.PAYOFF);

  if (!task) return null;

  return (
    <TaskContext.Provider value={task}>
      <EntityContext.Provider value={entity}>
        <ModalTask
          task={task}
          visible={isModalTaskVisible}
          handlerSetVisibile={setModalTaskVisible}
          disabledDots={false}
          canNavigateToStep={canNavigateToStep}
          responses={taskResponses}
          renderButtonTrigger={(openModal: any) => (
            <Button
              className={`button-open-modal button-open-modal-${task.state}`}
              onClick={() => openModal(true)}
            >
              {(task.state === TaskState.Review || task.state === TaskState.Approved) ? 'View task' :
                (task.state === TaskState.Incomplete) ? 'Complete task' :
                  (task.state === TaskState.RequestError) ? 'Resolve issues' :
                    'Begin Task'
              }
            </Button>
          )}
          header={{
            title: <span>{taskName.toUpperCase()}</span>,
            actionLeft: (
              isReview ? <StateTag state={TaskState.Review} size="large" /> :
                isApproved ? <StateTag state={TaskState.Approved} isLabelLocked={true} size="large" /> :
                  <img src={iconCortPng} alt="icon" className="paperPng icon-task-state" />
            ),
            /*     actionRight: (
                  <Button
                    className="button button-transparent"
                    onClick={() => onHelpDocs()}
                  >
                    <QuestionCircleOutlined />
                    Help docs
                  </Button>
                ) */
          }}
          animationDirection="onRight"
          stepsTotal={2}
          currentStep={step}
          setStep={setStep}
          tracker={renderTracker()}
        >
          <section className="children payoff">
            {step === 1 && (
              <Step1
                setStep={setStep}
                responseStep1={responseStep1}
                closeTask={() => setModalTaskVisible(false)}
              />
            )}
            {step === 2 && (
              <Step2
                setStep={setStep}
                closeTask={() => setModalTaskVisible(false)}
              />
            )}
          </section>
        </ModalTask>
      </EntityContext.Provider>
    </TaskContext.Provider>
  );
};
export default Payoff;
